// src/utils/tokenManager.ts

import { User, Authority } from "../types/auth.types";

// ─────────────────────────────────────────────
// Storage keys — using localStorage so tokens persist across
// browser restarts and tab closes. The user stays logged in
// until they explicitly click "Logout".
// ─────────────────────────────────────────────
const KEYS = {
  ACCESS_TOKEN: "auth_access_token",
  REFRESH_TOKEN: "auth_refresh_token",
  USER_DATA: "auth_user_data",
  AUTHORITY: "auth_authority",
};

export class TokenManager {
  // ─────────────────────────────────────────────
  // ACCESS TOKEN
  // ─────────────────────────────────────────────

  static setAccessToken(token: string): void {
    localStorage.setItem(KEYS.ACCESS_TOKEN, token);
  }

  static getToken(): string | null {
    return localStorage.getItem(KEYS.ACCESS_TOKEN);
  }

  // ─────────────────────────────────────────────
  // REFRESH TOKEN
  // ─────────────────────────────────────────────

  static setRefreshToken(token: string): void {
    localStorage.setItem(KEYS.REFRESH_TOKEN, token);
  }

  static getRefreshToken(): string | null {
    return localStorage.getItem(KEYS.REFRESH_TOKEN);
  }

  // ─────────────────────────────────────────────
  // USER DATA
  // ─────────────────────────────────────────────

  static setUserData(user: User): void {
    localStorage.setItem(KEYS.USER_DATA, JSON.stringify(user));
  }

  static getUserData(): User | null {
    const stored = localStorage.getItem(KEYS.USER_DATA);
    if (!stored) return null;
    try {
      return JSON.parse(stored) as User;
    } catch {
      return null;
    }
  }

  // ─────────────────────────────────────────────
  // AUTHORITY
  // ─────────────────────────────────────────────

  static setAuthority(authority: Authority): void {
    localStorage.setItem(KEYS.AUTHORITY, JSON.stringify(authority));
  }

  static getAuthority(): Authority | null {
    const stored = localStorage.getItem(KEYS.AUTHORITY);
    if (!stored) return null;
    try {
      return JSON.parse(stored) as Authority;
    } catch {
      return null;
    }
  }

  // ─────────────────────────────────────────────
  // TOKEN VALIDATION
  // ─────────────────────────────────────────────

  static isTokenValid(): boolean {
    const token = TokenManager.getToken();
    if (!token) return false;

    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const expiresAt = payload.exp * 1000;
      return Date.now() < expiresAt;
    } catch {
      return false;
    }
  }

  static getRemainingTime(): number {
    const token = TokenManager.getToken();
    if (!token) return 0;

    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const expiresAt = payload.exp * 1000;
      const remaining = Math.floor((expiresAt - Date.now()) / 1000);
      return Math.max(0, remaining);
    } catch {
      return 0;
    }
  }

  // ─────────────────────────────────────────────
  // CLEAR — called only on explicit user logout
  // ─────────────────────────────────────────────

  static clearAll(): void {
    localStorage.removeItem(KEYS.ACCESS_TOKEN);
    localStorage.removeItem(KEYS.REFRESH_TOKEN);
    localStorage.removeItem(KEYS.USER_DATA);
    localStorage.removeItem(KEYS.AUTHORITY);
  }

  static clearToken(): void {
    TokenManager.clearAll();
  }
}